export const CART_DEVICE_PRICE_TOLERANCE_KR = 1

/**
 * Prefer cart lineItemPrice when it matches the expected PDP device price.
 * Some carts (e.g. keep subscription) can return a different price context.
 */
export const resolveAdjustedDevicePrice = (expectedDiscountedPrice, cartPrice, tolerance = CART_DEVICE_PRICE_TOLERANCE_KR) => {
  if (cartPrice === null || cartPrice === undefined) {
    return expectedDiscountedPrice
  }
  const isCartPriceReasonable = Math.abs(cartPrice - expectedDiscountedPrice) <= tolerance
  if (isCartPriceReasonable) {
    return Math.max(0, cartPrice)
  }
  return expectedDiscountedPrice
}

const sumAppliedDiscountCodes = (appliedDiscountCodes) => {
  return appliedDiscountCodes.reduce((sum, code) => {
    return sum + (code.discountAmount || 0)
  }, 0)
}

const hasTotalDiscountOnCart = (cart) => {
  const total = cart.totalDiscountCodeDiscount
  return total !== undefined && total !== null
}

export const getVoucherDiscountFromCart = (cart) => {
  if (!cart) {
    return 0
  }
  if (hasTotalDiscountOnCart(cart)) {
    return cart.totalDiscountCodeDiscount
  }
  const codes = cart.appliedDiscountCodes
  if (!Array.isArray(codes) || codes.length === 0) {
    return 0
  }
  return sumAppliedDiscountCodes(codes)
}

export const cartHasVoucherDiscount = (cart) => {
  if (getVoucherDiscountFromCart(cart) > 0) {
    return true
  }
  return Array.isArray(cart?.appliedDiscountCodes) && cart.appliedDiscountCodes.length > 0
}

const getAppliedDiscountCodes = (cartOrCodes) => {
  if (Array.isArray(cartOrCodes)) {
    return cartOrCodes
  }
  return cartOrCodes?.appliedDiscountCodes
}

/** True when applied voucher uses a relative (percentage) cart discount. */
export const isPercentageVoucherDiscount = (cartOrCodes) => {
  const codes = getAppliedDiscountCodes(cartOrCodes)
  if (!Array.isArray(codes) || codes.length === 0) {
    return false
  }
  return codes.some(code => code.percentageDiscount === true)
}

const resolvePriceWithoutVoucher = (selectedPrice, lineItemPrice) => {
  if (lineItemPrice !== undefined && lineItemPrice !== null) {
    return lineItemPrice
  }
  return selectedPrice.basePrice || 0
}

const resolvePriceWithVoucher = (selectedPrice, lineItemPrice, voucherDiscount) => {
  const basePrice = selectedPrice.basePrice ?? 0
  const productDiscount = selectedPrice.discount ?? 0
  const expectedPrice = Math.max(0, basePrice - productDiscount - voucherDiscount)
  return resolveAdjustedDevicePrice(expectedPrice, lineItemPrice)
}

const getEffectiveVoucherDiscount = (cart, voucherDiscountOverride) => {
  return getVoucherDiscountFromCart(cart) || voucherDiscountOverride || 0
}

const hasAppliedVoucher = (cart, voucherDiscount) => {
  return cartHasVoucherDiscount(cart) || voucherDiscount > 0
}

export const resolveDevicePriceForCartItem = (cartItem, cart, voucherDiscountOverride = null) => {
  const selectedPrice = cartItem?.cartProduct?.selectedPrice
  if (!selectedPrice) {
    return 0
  }

  const lineItemPrice = selectedPrice.lineItemPrice ?? null
  const voucherDiscount = getEffectiveVoucherDiscount(cart, voucherDiscountOverride)
  if (!hasAppliedVoucher(cart, voucherDiscount)) {
    return resolvePriceWithoutVoucher(selectedPrice, lineItemPrice)
  }

  return resolvePriceWithVoucher(selectedPrice, lineItemPrice, voucherDiscount)
}

const isDeviceOrAccessory = (productTypeKey) => {
  return productTypeKey === 'device' || productTypeKey === 'accessory'
}

const patchSelectedPrice = (cartItem, resolvedPrice, pdpBundle) => {
  const selectedPrice = {
    ...cartItem.cartProduct.selectedPrice,
    lineItemPrice: resolvedPrice
  }
  if (pdpBundle?.basePrice !== undefined && pdpBundle?.basePrice !== null) {
    selectedPrice.basePrice = pdpBundle.basePrice
  }
  if (pdpBundle?.discount !== undefined && pdpBundle?.discount !== null) {
    selectedPrice.discount = pdpBundle.discount
  }
  return {
    ...cartItem,
    cartProduct: {
      ...cartItem.cartProduct,
      selectedPrice
    }
  }
}

/** Align device lineItemPrice on cart after load/update (MitID, voucher, receipt). */
export const patchDeviceLineItemPricesInCart = (cart, { resolveFromCartItem, getPdpProductBundlePrice }) => {
  if (!cart?.cartItemGroupList || !resolveFromCartItem) {
    return cart
  }

  const cartItemGroupList = cart.cartItemGroupList.map((group) => {
    if (!group?.cartItems) {
      return group
    }
    const cartItems = group.cartItems.map((cartItem) => {
      const productTypeKey = cartItem?.cartProduct?.productTypeKey
      if (!isDeviceOrAccessory(productTypeKey)) {
        return cartItem
      }
      const resolvedPrice = resolveFromCartItem(cartItem, cart)
      const pdpBundle = getPdpProductBundlePrice?.(cartItem) || null
      return patchSelectedPrice(cartItem, resolvedPrice, pdpBundle)
    })
    return { ...group, cartItems }
  })

  return { ...cart, cartItemGroupList }
}
